/**
 * El seguimiento de los documentos, como lo describe el contrato con el API.
 *
 * La lista (`list_document_reviews`) sigue a mano hasta que `pnpm api:types`
 * la traiga; el detalle (`get_document_review`) ya sale del tipo generado
 * (ver más abajo).
 */
import type { paths } from "../../lib/api-types.ts";

export const RUN_STATUSES = [
  "running",
  "waiting",
  "completed",
  "failed",
  "superseded",
] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];

/** Un resultado con su nombre: el tipo de archivo, el paso actual… */
export interface Named {
  readonly key: string;
  readonly label: string;
}

/* ------------------------------------------------------------------------ */
/* La lista: `list_document_reviews`                                          */
/* ------------------------------------------------------------------------ */

export interface InvoiceReviewFilters {
  readonly status?: RunStatus;
  /** El paso en el que está ahora. */
  readonly stepKey?: string;
  /** El resultado de «Identificar qué es el archivo» (la clasificación). */
  readonly photoType?: string;
  /** El resultado de «Revisar el archivo»: `image`, `pdf`… */
  readonly fileKind?: string;
  readonly documentId?: number;
  /** La bolsa: el mes de emisión (`AAAA-MM`) o el año entero (`AAAA`). */
  readonly period?: string;
  /**
   * La decisión final; `none` es «sin decidir» y `decided`, cualquiera de las
   * tres.
   */
  readonly decision?: "valid" | "invalid" | "for_data" | "none" | "decided";
  /** Quién decidió: su id de auth. */
  readonly decidedBy?: string;
  /** Decididos desde (incluido) y hasta (sin incluir), en ISO. */
  readonly decidedFrom?: string;
  readonly decidedTo?: string;
  /** Por qué falló (`FAILURE_REASONS`): solo tiene sentido con los fallidos. */
  readonly failureReason?: string;
}

/** La decisión final de una fila; `null` si no se decidió. */
export interface InvoiceReviewDecision {
  readonly outcome: string;
  /** «Válida», «Inválida»… */
  readonly label: string;
  /** El nombre de quien decidió (o su id, si no se sabe). */
  readonly by: string;
  readonly at: string;
}

export interface InvoiceReviewRow {
  readonly runId: string;
  readonly documentId: string;
  readonly flowVersion: number;
  readonly status: RunStatus;
  readonly statusLabel: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  /** Cuándo se subió. */
  readonly uploadedAt: string | null;
  readonly participantId: string | null;
  readonly resourceUrl: string;
  readonly currentStep: Named | null;
  readonly waitingSince: string | null;
  readonly fileKind: Named | null;
  /**
   * Lo que sugirió «Identificar qué es el archivo», con su confianza.
   * Ausente en un API de antes de la clasificación, y `null` en un PDF o si
   * el paso aún no corrió.
   */
  readonly photoType?: Named | null;
  /** `null` si algún precio es desconocido: NO es cero. */
  readonly costUsd: number | null;
  /** Opcional: un API de antes de `contrato-progreso.md` no la manda. */
  readonly decision?: InvoiceReviewDecision | null;
  /**
   * Por qué falló, si el seguimiento está fallido. Ausente en un API de
   * antes de reprocesar desde la página: entonces no se ofrece.
   */
  readonly failure?: Failure | null;
}

/**
 * Por qué falló un seguimiento, en palabras de un digitador. `reason` es una clasificación cerrada del error que
 * guardó el paso; las palabras las pone el back (`reasonLabel`).
 */
export const FAILURE_REASONS = [
  "model_limit",
  "model_unavailable",
  "timeout",
  "file",
  "invalid",
  "dispatch_lost",
  "other",
] as const;

export type FailureReason = (typeof FAILURE_REASONS)[number];

export interface Failure {
  readonly stepKey: string;
  readonly stepLabel: string;
  /** Una de `FAILURE_REASONS`; una que el front no conozca también vale. */
  readonly reason: string;
  readonly reasonLabel: string;
  /** Cuándo falló; `null` si no se sabe. */
  readonly at: string | null;
}

/** Por qué falló UN intento: va en cada intento fallido del detalle. */
export interface FailureReasonView {
  readonly reason: string;
  readonly reasonLabel: string;
}

export interface InvoiceReviewPage {
  readonly items: readonly InvoiceReviewRow[];
  readonly nextCursor: string | null;
  /** Sobre TODO lo que cumple los filtros, no solo la página. */
  readonly counts: {
    readonly total: number;
    readonly byStatus: Readonly<Record<string, number>>;
  };
}

/* ------------------------------------------------------------------------ */
/* El detalle: `get_document_review`                                          */
/* ------------------------------------------------------------------------ */

/**
 * El detalle completo de un documento en seguimiento, tal como lo manda el
 * API: su estado, cada intento con su respaldo, la ficha y el giro vigente.
 */
export type InvoiceReview =
  paths["/api/ccmas/v1/queries/get_document_review"]["get"]["responses"][200]["content"]["application/json"];

export type InvoiceReviewAttempt = InvoiceReview["attempts"][number];

export type AttemptStatus = InvoiceReviewAttempt["status"];

/**
 * Dónde está un intento programado en la cola de su tipo de paso: espera su
 * turno, un job lo está ejecutando, o falló una vez y espera para reintentar.
 */
export type AttemptQueue = NonNullable<InvoiceReviewAttempt["queue"]>;

export type QueueState = AttemptQueue["state"];

/** El respaldo: lo que vio el paso y por qué respondió eso. */
export type Evidence = NonNullable<InvoiceReviewAttempt["evidence"]>;
